/**
 * Assistente Qualità (QA26-F5.5): chat in un pannello laterale, risposte in streaming da POST /api/v1/assistente/chat
 * (proxy verso agent-runtime). Usa gli stessi strumenti dell'MCP con il perimetro del ruolo, in sola lettura. Il
 * pulsante compare solo se l'ambiente ha l'Assistente acceso (GET /auth/config → assistente). «Chiedi
 * all'assistente» nei suggerimenti di help-kit lo apre con la domanda già scritta, da confermare con Invio.
 * Dicitura «assistente AI» per l'AI Act, art. 50.
 */
import { useEffect, useRef, useState } from "react";
import { Alert, Button, Drawer, Grid, Input, Space, Spin, Tag, Typography } from "antd";
import { MessageOutlined, SendOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { subscribeAssistant } from "@brandart/help-kit";
import Markdown from "react-markdown";
import { ANCORA, isChiaveMessaggio, testoMessaggio, type Lingua } from "@qa/shared";
import { api, apiUrl } from "../api/client";
import { palette } from "../theme";
import { UI } from "./testi";
import { traccia } from "./usage";

interface Messaggio {
  chi: "io" | "assistente";
  testo: string;
  strumenti?: string[];
  errore?: boolean;
}

export function useAssistenteAttivo() {
  const q = useQuery({ queryKey: ["auth-config"], queryFn: () => api.get<{ assistente?: boolean }>("/auth/config"), staleTime: 5 * 60_000 });
  return !!q.data?.assistente;
}

export function AssistenteButton({ compatto, lingua }: { compatto: boolean; lingua: Lingua }) {
  const attivo = useAssistenteAttivo();
  const [open, setOpen] = useState(false);
  const [bozza, setBozza] = useState<{ testo: string } | null>(null);
  useEffect(() => {
    if (!attivo) return;
    return subscribeAssistant((req) => {
      setOpen(true);
      if (req.prompt) setBozza({ testo: req.prompt });
    });
  }, [attivo]);
  if (!attivo) return null;
  return (
    <>
      <Button
        type="text"
        icon={<MessageOutlined />}
        data-tip={ANCORA.assistente}
        aria-label={UI[lingua].assistente}
        onClick={() => {
          setOpen(true);
          traccia("assistant_opened", { source: "button" });
        }}
      >
        {compatto ? null : UI[lingua].assistente}
      </Button>
      <AssistentePanel open={open} onClose={() => setOpen(false)} bozza={bozza} lingua={lingua} />
    </>
  );
}

function AssistentePanel({ open, onClose, bozza, lingua }: { open: boolean; onClose: () => void; bozza: { testo: string } | null; lingua: Lingua }) {
  const narrow = Grid.useBreakpoint().md === false;
  const T = UI[lingua];
  const [messaggi, setMessaggi] = useState<Messaggio[]>([]);
  const [testo, setTesto] = useState("");
  const [inCorso, setInCorso] = useState(false);
  const sessione = useRef<string | null>(null);
  const fine = useRef<HTMLDivElement>(null);
  const abort = useRef<AbortController | null>(null);
  const msg = (key: unknown, params?: Record<string, string | number>) => (isChiaveMessaggio(key) ? testoMessaggio(key, params, lingua) : T.errore);

  useEffect(() => fine.current?.scrollIntoView({ block: "end" }), [messaggi]);
  useEffect(() => () => abort.current?.abort(), []);
  useEffect(() => {
    if (bozza && !inCorso) setTesto(bozza.testo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bozza]);

  const aggiornaUltimo = (f: (m: Messaggio) => Messaggio) => setMessaggi((ms) => [...ms.slice(0, -1), f(ms[ms.length - 1]!)]);

  async function invia(domanda: string) {
    const input = domanda.trim();
    if (!input || inCorso) return;
    setTesto("");
    setInCorso(true);
    setMessaggi((ms) => [...ms, { chi: "io", testo: input }, { chi: "assistente", testo: "", strumenti: [] }]);
    const ctrl = new AbortController();
    abort.current = ctrl;
    try {
      const res = await fetch(apiUrl("/assistente/chat"), {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ input, sessionId: sessione.current }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        const j = await res.json().catch(() => null);
        if (res.status === 404) sessione.current = null;
        throw new Error(msg(j?.error?.key, j?.error?.params));
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let i: number;
        while ((i = buf.indexOf("\n\n")) !== -1) {
          const frame = buf.slice(0, i);
          buf = buf.slice(i + 2);
          if (!frame.startsWith("data:")) continue;
          const e = JSON.parse(frame.slice(5)) as { type: string; text?: string; name?: string; sessionId?: string; key?: string };
          if (e.type === "delta") aggiornaUltimo((m) => ({ ...m, testo: m.testo + (e.text ?? "") }));
          else if (e.type === "tool" && e.name) aggiornaUltimo((m) => ({ ...m, strumenti: [...(m.strumenti ?? []), e.name!] }));
          else if (e.type === "final") {
            if (e.sessionId) sessione.current = e.sessionId;
            if (e.text) aggiornaUltimo((m) => ({ ...m, testo: e.text! }));
          } else if (e.type === "error") aggiornaUltimo((m) => ({ ...m, testo: m.testo || msg(e.key), errore: !m.testo }));
        }
      }
    } catch (err) {
      if (!ctrl.signal.aborted) aggiornaUltimo((m) => ({ ...m, testo: err instanceof Error ? err.message : T.errore, errore: true }));
    } finally {
      setInCorso(false);
      abort.current = null;
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={narrow ? "100%" : 520}
      title={
        <div>
          <div>{T.titolo}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            {T.sottotitolo}
          </Typography.Text>
        </div>
      }
      extra={
        messaggi.length > 0 && (
          <Button
            size="small"
            disabled={inCorso}
            onClick={() => {
              setMessaggi([]);
              sessione.current = null;
            }}
          >
            {T.nuova}
          </Button>
        )
      }
      styles={{ body: { display: "flex", flexDirection: "column", padding: 0 } }}
    >
      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {messaggi.length === 0 && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Alert type="info" showIcon message={T.intro} />
            <Typography.Text type="secondary">{T.perEsempio}</Typography.Text>
            {T.esempi.map((d) => (
              <Button key={d} block style={{ textAlign: "left", height: "auto", whiteSpace: "normal" }} onClick={() => invia(d)}>
                {d}
              </Button>
            ))}
          </Space>
        )}
        {messaggi.map((m, k) => (
          <div key={k} data-sensitive style={{ display: "flex", justifyContent: m.chi === "io" ? "flex-end" : "flex-start", marginBottom: 12 }}>
            <div style={{ maxWidth: "88%", padding: "8px 12px", borderRadius: 10, background: m.chi === "io" ? palette.primary : palette.sunk, color: m.chi === "io" ? "#fff" : undefined }}>
              {m.chi === "io" ? (
                m.testo
              ) : m.testo ? (
                m.errore ? (
                  <Typography.Text type="danger">{m.testo}</Typography.Text>
                ) : (
                  <Markdown>{m.testo}</Markdown>
                )
              ) : (
                <Spin size="small" />
              )}
              {m.strumenti && m.strumenti.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  {[...new Set(m.strumenti)].map((s) => (
                    <Tag key={s} style={{ fontSize: 11 }}>
                      {s}
                    </Tag>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={fine} />
      </div>
      <div style={{ borderTop: `1px solid ${palette.hair}`, padding: 12, display: "flex", gap: 8 }}>
        <Input.TextArea
          autoFocus
          value={testo}
          onChange={(e) => setTesto(e.target.value)}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              void invia(testo);
            }
          }}
          autoSize={{ minRows: 1, maxRows: 5 }}
          maxLength={4000}
          placeholder={T.placeholder}
          disabled={inCorso}
        />
        <Button type="primary" icon={<SendOutlined />} onClick={() => invia(testo)} loading={inCorso} aria-label={T.invia} />
      </div>
    </Drawer>
  );
}
